import { useState, useRef } from "react";
import { getGrade, formatNumber, Badge, StatusBadge, CTATeaser } from "./DashboardShared";

// =====================================================
// TAB: RATE PARITY
// =====================================================
const RateParityTab = ({ paritySnapshot, currency, onTabChange }) => {
  const scrollRef = useRef(null);
  const [showFade, setShowFade] = useState(true);
  
  const score = paritySnapshot?.score || 0;
  const scoreGrade = getGrade(score);
  const sampleDates = paritySnapshot?.sampleDates || {};
  const directPrice = paritySnapshot?.directPrice;
  const weekendDirectPrice = paritySnapshot?.weekendDirectPrice || directPrice; // Fallback to weekday direct if no weekend direct
  
  // Convert weekday_pricing to array format
  // Backend returns dict: {seller: price} 
  // Mock data may have array: [{seller, price}]
  const rawWeekdayPricing = paritySnapshot?.weekday_pricing;
  const rawWeekendPricing = paritySnapshot?.weekend_pricing;
  
  const convertPricing = (raw) => {
    if (!raw) return [];
    // If already an array, return as-is
    if (Array.isArray(raw)) return raw;
    // If dict, convert to array
    return Object.entries(raw).map(([seller, price]) => ({ seller, price }));
  };
  
  const weekdayPricing = convertPricing(rawWeekdayPricing);
  const weekendPricing = convertPricing(rawWeekendPricing);
  
  const handleScroll = (e) => {
    const el = e.target;
    const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
    setShowFade(!isAtEnd);
  };
  
  const getStatus = (price, directPrice) => {
    if (!price || !directPrice) return "na";
    const deviation = ((price - directPrice) / directPrice) * 100;
    if (Math.abs(deviation) < 1) return "parity";
    if (deviation < 0) return "undercut";
    return "premium";
  };
  
  const getDeviation = (price, directPrice) => {
    if (!price || !directPrice) return null;
    return ((price - directPrice) / directPrice) * 100;
  };
  
  const formatPrice = (price) => {
    if (!price) return "N/A";
    return `${currency} ${formatNumber(Math.round(price))}`;
  };
  
  const RateTable = ({ title, date, pricing, directPrice: tableDirectPrice }) => (
    <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderLeft: "3px solid #2D4DE0", borderRadius: 14, padding: 20, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 700, color: "#0A0A1A" }}>{title}</span>
        <span style={{ fontSize: 10.5, color: "rgba(10,10,26,0.35)" }}>Check-in: {date}</span>
      </div>
      
      <div className="rate-scroll-container">
        <div className="rate-scroll-inner" onScroll={handleScroll} ref={scrollRef}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.8fr 0.7fr", alignItems: "center", padding: "0 0 8px", borderBottom: "1px solid rgba(10,10,26,0.08)", marginBottom: 4, minWidth: 400 }}>
            {["Channel", "Rate", "Deviation", "Status"].map(h => (
              <span key={h} style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(10,10,26,0.35)" }}>{h}</span>
            ))}
          </div>
          
          {/* Direct Website Row */}
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.8fr 0.7fr", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(10,10,26,0.05)", minWidth: 400 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0A0A1A" }}>Direct Website</span>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: "#0A0A1A" }}>{tableDirectPrice ? formatPrice(tableDirectPrice) : "N/A"}</span>
            <span style={{ fontSize: 12, color: "rgba(10,10,26,0.3)" }}>—</span>
            <StatusBadge status={tableDirectPrice ? "reference" : "na"} />
          </div>
          
          {/* OTA Rows */}
          {pricing.slice(0, 5).map((item, i) => {
            const deviation = getDeviation(item.price, tableDirectPrice);
            const status = getStatus(item.price, tableDirectPrice);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.8fr 0.7fr", alignItems: "center", padding: "10px 0", borderBottom: i < pricing.length - 1 ? "1px solid rgba(10,10,26,0.04)" : "none", minWidth: 400 }}>
                <span style={{ fontSize: 12.5, color: "rgba(10,10,26,0.6)" }}>{item.seller || item.channel || "OTA"}</span>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 600, color: "rgba(10,10,26,0.6)" }}>{formatPrice(item.price)}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: deviation !== null ? (deviation < 0 ? "#DC2626" : deviation > 0 ? "#EA580C" : "rgba(10,10,26,0.3)") : "rgba(10,10,26,0.3)" }}>
                  {deviation !== null ? `${deviation > 0 ? "+" : ""}${deviation.toFixed(1)}%` : "N/A"}
                </span>
                <StatusBadge status={status} />
              </div>
            );
          })}
        </div>
        <div className={`rate-fade-overlay ${!showFade ? 'hidden' : ''}`} />
      </div>
    </div>
  );
  
  return (
    <div>
      <div className="print-section-title">Rate Parity</div>
      {/* Rate Parity Header */}
      <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderLeft: "3px solid #2D4DE0", borderRadius: 14, marginBottom: 14, display: "flex", alignItems: "center", gap: 16, padding: 20 }}>
        <div style={{ position: "relative", width: 70, height: 70, flexShrink: 0 }}>
          <svg width="70" height="70" viewBox="0 0 70 70" style={{ position: "absolute", top: 0, left: 0 }}>
            <circle cx="35" cy="35" r="28" fill="none" stroke="#F5F6FA" strokeWidth="5"/>
            <circle cx="35" cy="35" r="28" fill="none" stroke="#2D4DE0" strokeWidth="5"
              strokeDasharray={2 * Math.PI * 28} strokeDashoffset={2 * Math.PI * 28 * (1 - score / 10)}
              strokeLinecap="round" transform="rotate(-90 35 35)"/>
          </svg>
          <div style={{ position: "absolute", top: 0, left: 0, width: 70, height: 70, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 800, color: "#0A0A1A", lineHeight: 1 }}>{score}</span>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 500, color: "rgba(10,10,26,0.3)", marginTop: 2 }}>out of 10</span>
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 700, color: "#0A0A1A", marginBottom: 2 }}>Rate Parity Status</div>
          <Badge grade={scoreGrade} />
          <div style={{ fontSize: 11.5, color: "rgba(10,10,26,0.4)", marginTop: 6, lineHeight: 1.5 }}>Rate parity measures price consistency across your direct website and OTA channels.</div>
        </div>
      </div>
      
      {/* Rate Tables */}
      {weekdayPricing.length > 0 && (
        <RateTable 
          title="Weekday Rates" 
          date={sampleDates?.weekday?.checkIn || "N/A"} 
          pricing={weekdayPricing}
          directPrice={directPrice}
        />
      )}
      
      {weekendPricing.length > 0 ? (
        <RateTable 
          title="Weekend Rates" 
          date={sampleDates?.weekend?.checkIn || "N/A"} 
          pricing={weekendPricing}
          directPrice={weekendDirectPrice}
        />
      ) : weekdayPricing.length > 0 && (
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderRadius: 14, padding: 20, marginBottom: 14 }}>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 700, color: "#0A0A1A", marginBottom: 8 }}>Weekend Rates</div>
          <div style={{ fontSize: 12.5, color: "rgba(10,10,26,0.4)" }}>Weekend rates not available for this property.</div>
        </div>
      )}
      
      {weekdayPricing.length === 0 && weekendPricing.length === 0 && (
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderRadius: 14, padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "rgba(10,10,26,0.5)", marginBottom: 8 }}>
            {paritySnapshot?.criticalMessage || "No rate data available"}
          </div>
          <div style={{ fontSize: 12, color: "rgba(10,10,26,0.35)" }}>
            Rate parity analysis requires your hotel to be listed on Google Hotels.
          </div>
        </div>
      )}
      
      <CTATeaser onTabChange={onTabChange} />
    </div>
  );
};

export default RateParityTab;
